import { sortByField } from '@federa/const/sort-comparators';
import { SORT_DIRECTION } from '@federa/const/sort-direction';
import { recordInstantMs } from '@federa/const/record-instant-order';
import { normalizeSqliteUtcTimestamp } from '@federa/const/sqlite-timestamp';
import type { SortDirection } from '@federa/types/primitives-status';

/**
 * Extract a numeric timestamp for sorting: the numeric value (epoch ms or sequence number) when present, else the
 * parsed date string, else 0.
 *
 * The string is stamped UTC through `normalizeSqliteUtcTimestamp` (`const/sqlite-timestamp.ts`) BEFORE parsing, the
 * same read-side correction `parseDate` applies -- the fix lands on this one call so every caller inherits it instead
 * of each re-deciding. It is load-bearing for the two D1-sourced callers: the ledger sort keys on `transaction_date` /
 * `added_time` (`components/lib/api/functions.ledger.ts`) and the chat-room sorts key on `last_message_time`, which
 * `normalizeTimestamp` (`tools/conversations/conversation-response-builders.ts`) passes through verbatim -- both are
 * `datetime('now')` wall clocks that a bare `Date.parse` reads in the VIEWER's zone, silently reordering the list for
 * anyone off UTC. The bid sort (`components/lib/websocket/parse.ts`) already holds a `toISOString()` product, so the call is a
 * byte-for-byte passthrough there; the normalizer only ever touches the zone-less space-separated shape.
 */
export function extractTimestamp(numericValue: number | null | undefined, dateString: string | null | undefined): number {
	if (numericValue != null) {
		return numericValue;
	}
	if (dateString) {
		return recordInstantMs(normalizeSqliteUtcTimestamp(dateString)) ?? 0;
	}
	return 0;
}

/** Sort an array by a numeric accessor with configurable direction ('desc' = most recent first), returning a new array. The canonical utility for timestamp-based ordering. */
function sortByTimestamp<T>(items: T[], accessor: (item: T) => number, direction: SortDirection): T[] {
	return [...items].sort(sortByField(accessor, direction));
}

/** Sort an array by timestamp descending (most recent first) -- convenience wrapper around {@link sortByTimestamp}. */
export function sortByTimestampDesc<T>(items: T[], accessor: (item: T) => number): T[] {
	return sortByTimestamp(items, accessor, SORT_DIRECTION.DESC);
}

/** Sort an array by timestamp ascending (oldest first) -- convenience wrapper around {@link sortByTimestamp}. */
export function sortByTimestampAsc<T>(items: T[], accessor: (item: T) => number): T[] {
	return sortByTimestamp(items, accessor, SORT_DIRECTION.ASC);
}

/** Oldest first by a row's `createdAt` instant -- the order a message thread reads in. */
export function sortByCreatedAtAsc<T extends { readonly createdAt: string }>(items: T[]): T[] {
	return sortByTimestampAsc(items, (item) => new Date(item.createdAt).getTime());
}
